<template>
  <AdminLayout>
    <section class="flex items-start px-14 space-x-9">
      <div class="w-full">
        <div class="mt-12">
          <div class="border rounded-lg py-7 px-8">
            <div class="flex justify-between mb-5">
              <p class="font-semibold text-xl text-neutral-800">Calculate Net Pay</p>
            </div>

            <div class="flex mb-8 items-center bg-neutral-300/50 rounded-md p-1">
              <div class="ml-auto" v-if="userType === 'admin'">
                <label class="text-sm leading-none font-normal text-black mr-2">Filter:</label>
                <select class="border border-white text-sm rounded-full py-1 px-2" v-model="slectedLocation"
                  @change="fetchLeaveRequests">
                  <option value="">Location</option>
                  <option v-for="branch in branchList" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
                </select>
              </div>
              <div class="w-96 mr-3 ml-auto">
                <input type="search" v-model="searchQuery"
                  class="w-full rounded-full bg-white text-sm px-6 py-3 outline-none block font-normal"
                  placeholder="Search...">
              </div>
            </div>

            <div class="overflow-x-auto">
              <div class="min-w-[2620px]">
                <div class="grid grid-cols-20 bg-[#ECF6FF] divide-x divide-[#eaeaea]">
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">Employee Id</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal col-span-2">Employee Name
                  </div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">Basic Salary</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">HRA</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">Other Allowance</div>

                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">Gross Pay</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">EPF Slab</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">EPF Amount</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">ESIC</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">Advance</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">Professional Tax</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">EPS Employer</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">ER</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">ESIC2</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">Net Pay</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal">CTC</div>
                  <div class="text-sm leading-none px-2 py-3 text-[#404A5A] flex items-center font-normal justify-center col-span-2">
                    Action</div>
                </div>
                <template v-if="isLoading">
                  <div v-for="n in 5" :key="'skeleton-' + n"
                    class="grid grid-cols-20 bg-white divide-x-[1px] divide-white rounded-md shadow mt-1.5 animate-pulse">
                    <div v-for="i in 20" :key="i" class="px-2 py-2">
                      <div class="h-4 bg-gray-300 rounded w-full"></div>
                    </div>
                  </div>
                </template>

                <!-- Actual Data Rows (Shown when loading is false) -->
                <template v-else>
                  <div v-if="paginatedData.length === 0" class="text-center py-6 text-neutral-500 text-sm">
                    No data found.
                  </div>
                  <div v-for="(employee, index) in paginatedData" :key="index"
                  class="grid grid-cols-20 bg-white divide-x divide-y divide-[#eaeaea]">
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal border-t border-[#eaeaea]">{{ employee.empid }}</div>
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal col-span-2">{{ employee.fname
                    }} {{
                    employee.lname }}</div>
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.basic }}</div>
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.hra }}</div>
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.other_allowance
                    }}</div>

                  <!-- <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.gross_salary }}</div> -->

                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">
                    <input v-model.number="employee.gross_pay" type="number"
                      class="border rounded px-1 py-0.5 text-sm w-24" @change="calculateSalary(employee)" />
                  </div>
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">
                    <select v-model="employee.use_epf_slab" class="text-sm border rounded px-1 py-0.5">
                      <option :value="true">Yes</option>
                      <option :value="false">No</option>
                    </select>
                  </div>

                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.pf_amount }}
                  </div>
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.esic }}</div>
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal"> <input
                      v-model.number="employee.advance" type="number" class="border rounded px-1 py-0.5 text-sm w-24"
                      @change="calculateSalary(employee)" /></div>
                  <!-- Advance -->
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.ptax }}</div>
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.eps }}</div>
                  <!-- EPS Employer -->
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.er }}</div>
                  <!-- ER -->
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.esic2 }}</div>
                  <!-- ESIC2 -->
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.net_pay }}</div>
                  <div class="text-sm leading-none px-2 py-2 flex items-center font-normal">{{ employee.ctc }}</div>

                  <div
                    class="text-sm leading-none px-2 pb-2 flex items-center font-normal justify-center col-span-2 space-x-2">
                    <button @click="handleCalculate(employee)" :disabled="employee.isCalculating"
                      class="text-white bg-blue-400 rounded-md px-3 py-1.5 text-xs font-normal">
                      <span v-if="employee.isCalculating">Calculating...</span>
                      <span v-else>Calculate</span>
                    </button>

                    <button @click="handleUpdate(employee)"
                      class="inline-flex items-center justify-center text-white bg-green-500 rounded-md px-3 py-1.5 text-xs font-normal"
                      :disabled="employee.isUpdating">
                      <span v-if="employee.isUpdating">Updating...</span>
                      <span v-else>Update</span>
                    </button>

                  </div>

                  </div>
                <PaginationComponent :totalItems="total" :itemsPerPage="itemsPerPage" v-model:currentPage="currentPage" />
                </template>
              

              </div>
            </div>

           
          </div>
        </div>
      </div>
      <!-- <RightSideBar /> -->
    </section>
  </AdminLayout>
</template>

<script>
import AdminLayout from '../AdminDashboard/AdminLayout.vue';
// import RightSideBar from '../../components/RightSideBar.vue';
import PaginationComponent from "../../components/PaginationComponent.vue";
import axios from 'axios';
import { BASE_URL } from "../../config";
import debounce from 'lodash/debounce';
import { createToast } from 'mosha-vue-toastify';
import { createLocal } from 'the-storages'
const mirror = createLocal();
const storage = mirror._prx;



export default {
  name: 'LeaveListing',
  components: {
    AdminLayout,
    // RightSideBar,
    PaginationComponent
  },
  data() {
    return {
      data: [],
      itemsPerPage: 10,
      currentPage: 1,
      total: 0,
      searchQuery: null,
      salaryConfig: null,
      storage: storage,
      branchList: [],
      slectedLocation: '',
      userType: '',
      isLoading: true,
      previousLocation: '', // Add this to track location changes
    };
  },
  computed: {
    paginatedData() {
      return this.data;
    }

  },
  watch: {
    searchQuery() {
      this.onSearch();
    },
    currentPage(newPage) {
      this.$router.push({ query: { ...this.$route.query, page: newPage } }); // ← add this
      this.fetchLeaveRequests();
    }
  },
  methods: {
    tostMessage(type, message) {
      createToast({ description: message }, {
        type,
        timeout: 2500,
        position: 'bottom-right',
        showIcon: true
      });
    },

    async getBranchList() {
      const formData = {
        "company_id": this.storage.companyDetails.id
      };
      await axios.post(BASE_URL + '/get-emp-branch-list', formData)
        .then((response) => {
          this.branchList = response.data.branchDetails;
          console.log("Role LIST:: ", response.data);
        })
    },



    async fetchSalaryConfig() {
      try {
        const formData = {
          "company_id": localStorage.getItem('company_id')
        };
        const response = await axios.post(BASE_URL + '/get-payroll-data', formData);
        console.log('payroll data', response.data);//here is the response i have provided to you
        if (response.data && response.data.payrollData.length > 0) {
          const payrollRecord = response.data.payrollData[0];

          // Assign values, parsing JSON where needed
          this.salaryConfig = {
            id: payrollRecord.id,
            company_id: payrollRecord.company_id,
            basic: payrollRecord.basic,
            hra: payrollRecord.hra,
            allowances: JSON.parse(payrollRecord.allowances || '[]'),
            esi: JSON.parse(payrollRecord.esi || '{"enabled": "false" }'),
            pf: JSON.parse(payrollRecord.pf || '{"enabled": "false" }'),
            ptax: JSON.parse(payrollRecord.ptax || '[]')
          };
        } else {
          //this.isEditing = false;
        }
      } catch (error) {
        console.error('Error fetching payroll data:', error);
        alert('Error fetching payroll data!');
      }
    },

    async fetchLeaveRequests() {
      this.isLoading = true;
      try {
         // Only reset page if location changed (not just when it has a value)
         if (this.slectedLocation !== this.previousLocation) {
                    this.currentPage = 1;
                    this.previousLocation = this.slectedLocation;
                }
        const response = await axios.get(`${BASE_URL}/get-active-employees`, {
          params: {
            limit: this.itemsPerPage,
            page: this.currentPage,
            name: this.searchQuery,
            location: this.slectedLocation,
          }
        });
        //   const pfEnabled = this.salaryConfig?.pf?.enabled === 'true';
        //   alert(pfEnabled);
        //   this.data = response.data.data.map(emp => ({
        //     ...emp,
        //     basic: 0,
        //     hra: 0,
        //     other_allowance: 0,
        //     gross_pay: emp.gross_salary || 0,
        //     pf_amount: 0,
        //     esic: 0,
        //     ptax: 0,
        //     net_pay: 0,
        //     ctc: 0,
        //     eps: 0,
        //     er: 0,
        //     esic2:0,
        //     advance:0,
        //     use_epf_slab: pfEnabled, // default to "Yes"
        //   }));
        this.data = response.data.data.map(emp => {
          const salary = emp.employee_salary || {};

          const pfEnabled = this.salaryConfig?.pf?.enabled === 'true';

          return {
            ...emp,
            basic: salary.basic ?? 0,
            hra: salary.hra ?? 0,
            other_allowance: salary.other_allowance ?? 0,
            gross_pay: +salary.gross_pay || emp.gross_salary || 0,
            pf_amount: salary.epf_amount ?? 0,
            esic: salary.esic ?? 0,
            ptax: salary.ptax ?? 0,
            eps: salary.eps ?? 0,
            er: salary.er ?? 0,
            esic2: salary.esic2 ?? 0,
            net_pay: salary.netpay ?? 0,
            ctc: salary.ctc ?? 0,
            advance: salary.advance ?? 0,
            use_epf_slab: salary.epf_slab ?? pfEnabled,
            isCalculating: false,
            isUpdating: false
          };
        });
        this.total = response.data.total;
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        this.isLoading = false;
      }
    },
    calculateSalary(emp) {
      console.log(emp);

      const config = this.salaryConfig;
      const gross = parseFloat(emp.gross_pay) || 0;

      // Basic and HRA calculation
      emp.basic = +(gross * (+config.basic / 100)).toFixed(2);
      emp.hra = +(gross * (+config.hra / 100)).toFixed(2);

      // Other Allowances calculation
      let allowanceTotal = 0;
      config.allowances.forEach(allow => {
        allowanceTotal += gross * (+allow.percentage / 100);
      });
      emp.other_allowance = +allowanceTotal.toFixed(2);

      // Educational Allowance (assumed part of config or 0)
      emp.edu_allow = emp.edu_allow || 0;

      // Sum for PF base
      const pfBase = emp.basic + emp.other_allowance + emp.edu_allow;

      // PF Amount based on slab logic
      let pfamount = 0;
      if (config.pf.enabled === 'true' && emp.use_epf_slab) {
        if (pfBase <= 15000) {
          pfamount = +((pfBase * (12 / 100)).toFixed(2));
        } else {
          pfamount = 1800;
        }
      }
      emp.pf_amount = pfamount;

      // ESI Calculation
      emp.esic = config.esi.enabled === 'true' && gross <= 21000
        ? +(gross * 0.0075).toFixed(2)
        : 0;

      // Employer ESI (ESIC2)
      emp.esic2 = config.esi.enabled === 'true' && gross <= 21000
        ? +(gross * 0.0325).toFixed(2)
        : 0;

      // Professional Tax (PTax)
      emp.ptax = 0;
      for (let slab of config.ptax) {
        if (gross >= +slab.from && gross <= +slab.to) {
          emp.ptax = +slab.amount;
          break;
        }
      }

      // Gross
      emp.gross = +(emp.basic + emp.hra + emp.other_allowance + emp.edu_allow).toFixed(2);

      // EPS and ER
      emp.eps = +((pfamount * 8.33) / 12).toFixed(2);
      emp.er = +((pfamount * 3.67) / 12).toFixed(2);

      // Net Pay
      emp.net_pay = +(emp.gross - emp.pf_amount - emp.esic - emp.ptax - emp.advance).toFixed(2);

      // CTC
      emp.ctc = +(
        emp.gross + emp.eps + emp.er + emp.esic2
      ).toFixed(2);
    },


    async updateSalaryRecord(emp) {
      try {
        const payload = {
          company_id: localStorage.getItem('company_id'),
          employee_id: emp.uid,
          fname: emp.fname,
          lname: emp.lname,
          basic: emp.basic,
          hra: emp.hra,
          other_allowance: emp.other_allowance,
          gross_pay: emp.gross_pay,
          epf_slab: emp.use_epf_slab,
          epf_amount: emp.pf_amount,
          esic: emp.esic,
          ptax: emp.ptax,
          eps: emp.eps,
          er: emp.er,
          esic2: emp.esic2,
          netpay: emp.net_pay,
          ctc: emp.ctc,
          advance: emp.advance,
        };

        const response = await axios.post(`${BASE_URL}/employee-salary`, payload);
        this.tostMessage('success', response.data.message || 'Salary record updated');
      } catch (error) {
        if (error.response && error.response.data.errors) {
          const messages = Object.values(error.response.data.errors).flat().join('\n');
          this.tostMessage('danger', `Validation Error:\n${messages}`);
        } else {
          console.error('Error updating salary:', error);
          this.tostMessage('danger', 'Failed to update salary record.');
        }
      }
    },
    handleCalculate(emp) {
      emp.isCalculating = true;
      try {
        this.calculateSalary(emp);
      } finally {
        emp.isCalculating = false;
      }
    },

    async handleUpdate(emp) {
      emp.isUpdating = true;
      try {
        await this.updateSalaryRecord(emp);
      } finally {
        emp.isUpdating = false;
      }
    },


    onSearch() {
      this.currentPage = 1;
      this.fetchLeaveRequests();
    }
  },
  created() {
    const page = parseInt(this.$route.query.page) || 1;
        this.currentPage = page;
    if (this.storage.userType === 'Super Admin') {
      this.slectedLocation = ''; // Set to an empty string or a default value
      this.userType = 'admin';
    } else {
      this.slectedLocation = this.storage.employeeDetails.branch; // Assign only if branch is valid
      this.userType = 'not-admin';
      console.log('branch', this.slectedLocation);
    }
    this.getBranchList();
    this.onSearch = debounce(this.onSearch, 400);
  },
  async mounted() {
    await this.fetchSalaryConfig();
    this.fetchLeaveRequests();
  }
};
</script>

<style scoped>
.grid-cols-20 {
  grid-template-columns: repeat(20, minmax(0, 1fr));
}

.loader {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>